import { Fragment, PropsWithChildren, ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { SearchInput, combineClassnames, useOutsideClick } from 'app/common/presentation'
import { DropDownIcon } from 'app/common/presentation/icons'

import { Menu } from 'components'
import ActivityIndicator from 'components/ActivityIndicator/ActivityIndicator'
import { Button } from 'components/Button'
import { Checkbox } from 'components/Checkbox'

import styles from './DropDown.module.scss'

interface DropDownValue<T> {
  count?: number
  icon?: ReactNode
  label: string
  value: T
}

const isDropDownValue = <T,>(value: unknown): value is DropDownValue<T> =>
  (value as DropDownValue<T>).label !== undefined && (value as DropDownValue<T>).value !== undefined

interface CustomDropDownProps<T> {
  data: T[]
  renderValueContent: (value: T) => JSX.Element | string
}

interface DefaultDropDownProps<T> {
  data: DropDownValue<T>[]
  renderValueContent?: never
}

interface StringDropDownProps<T> {
  data: Extract<T, string>[]
  renderValueContent?: never
}

interface BaseDropDownProps<T> extends PropsWithChildren {
  className?: string
  classNameMenu?: string
  loading?: boolean
  onSelect: (selected: T) => void
  small?: boolean
  placeholder?: string
}

export type DropDownProps<T> = BaseDropDownProps<T> &
  (CustomDropDownProps<T> | DefaultDropDownProps<T> | StringDropDownProps<T>) & {
    selected: T | null
  }

export interface MultipleDropDownProps<T> extends BaseDropDownProps<T> {
  data: (DropDownValue<T> | T)[]
  onReset?: () => void
  renderValueContent?: (value: T) => JSX.Element | string
  selection: T[]
  type: 'multiple'
}

const renderValue = <T,>(
  value: DropDownValue<T> | T,
  renderValueContent?: (value: T) => JSX.Element | string,
) => {
  if (isDropDownValue(value)) {
    return (
      <>
        {value.icon && <span className={styles.valueIcon}>{value.icon}</span>}
        <span>{value.label}</span>
        {value.count !== undefined && <span className={styles.count}>{value.count}</span>}
      </>
    )
  }

  if (renderValueContent) {
    const valueContent = renderValueContent(value)
    return typeof valueContent === 'string' ? <span>{valueContent}</span> : valueContent
  }

  if (typeof value === 'string') {
    return <span>{value}</span>
  }

  // should normally not happen => use 'N/A' as indicator if so
  return 'N/A'
}

const renderSelectedValue = <T,>(
  value: DropDownValue<T> | T,
  renderValueContent?: (value: T) => JSX.Element | string,
) => {
  if (!isDropDownValue(value)) return renderValue(value, renderValueContent)

  return (
    <>
      {value.icon && <span className={styles.valueIcon}>{value.icon}</span>}
      <span>{value.label}</span>
      {value.count !== undefined && <span className={styles.count}>{value.count}</span>}
    </>
  )
}

const DropDown = <T,>(props: DropDownProps<T> | MultipleDropDownProps<T>) => {
  const {
    children,
    className,
    classNameMenu,
    data,
    loading,
    onSelect,
    renderValueContent,
    small,
    placeholder,
  } = props
  const { t } = useTranslation('components')

  const [open, setOpen] = useState<undefined | 'down' | 'up'>()
  const [search, setSearch] = useState('')

  const ref = useOutsideClick<HTMLDivElement>(() => setOpen(undefined))

  const isMultipleSelection = 'selection' in props
  const selectedValues = isMultipleSelection
    ? (data.filter((entry) =>
        props.selection.includes(isDropDownValue(entry) ? entry.value : entry),
      ) ?? [])
    : (data.filter((entry) => props.selected === (isDropDownValue(entry) ? entry.value : entry)) ??
      [])

  const filteredData = useMemo(
    () =>
      data.filter((entry) => {
        const searchableValue = isDropDownValue(entry)
          ? `${entry.label} ${String(entry.value)}`
          : (JSON.stringify(entry) ?? String(entry))

        return searchableValue.toLocaleLowerCase().includes(search.toLocaleLowerCase())
      }),
    [data, search],
  )
  const hasCount = selectedValues.some(
    (selectedValue) => isDropDownValue(selectedValue) && selectedValue.count !== undefined,
  )

  useEffect(() => {
    if (open && ref.current) {
      const dropdownRect = ref.current.getBoundingClientRect()
      const menu = ref.current.querySelector<HTMLElement>('[role="menu"]')
      if (menu) {
        const menuHeight = menu.clientHeight
        const spaceAtTheBottom =
          document.documentElement.clientHeight -
          dropdownRect.top -
          dropdownRect.height -
          menuHeight
        const spaceAtTheTop = dropdownRect.top - menuHeight

        const upward = spaceAtTheBottom < 0 && spaceAtTheTop > spaceAtTheBottom
        setOpen(upward ? 'up' : 'down')
      }
    }
  }, [open, ref])

  const handleSelect = (value: T | DropDownValue<T>) => {
    onSelect(isDropDownValue(value) ? value.value : value)
    if (!isMultipleSelection) {
      setOpen(undefined)
    }
  }

  return (
    <div
      className={[
        styles.dropDown,
        small ? styles.small : undefined,
        data.length ? undefined : styles.disabled,
        className,
      ].join(' ')}
      ref={ref}
    >
      {children ? (
        <div onClick={() => setOpen(open ? undefined : 'down')}>{children}</div>
      ) : (
        <div
          className={[
            styles.select,
            open ? styles.focused : undefined,
            hasCount ? styles.filled : undefined,
          ].join(' ')}
          onClick={() => setOpen(open ? undefined : 'down')}
        >
          <div
            className={[
              styles.selection,
              selectedValues.length ? undefined : styles.placeholder,
            ].join(' ')}
          >
            {selectedValues.length
              ? selectedValues.map((selectedValue, index) => (
                  <Fragment key={index}>
                    <>
                      {renderSelectedValue(selectedValue, renderValueContent)}
                      {index < selectedValues.length - 1 && <span>,&nbsp;</span>}
                    </>
                  </Fragment>
                ))
              : (placeholder ?? '-')}
          </div>
          {loading ? <ActivityIndicator size={'xxs'} /> : <DropDownIcon />}
        </div>
      )}

      {open && (
        <Menu.Menu
          className={combineClassnames(styles.menu, classNameMenu)}
          style={{
            left: children ? 'unset' : 0,
            ...(open === 'up' ? { bottom: 42 } : undefined),
          }}
        >
          {isMultipleSelection &&
            (children && placeholder ? (
              <div className={styles.title}>{placeholder}</div>
            ) : (
              <SearchInput
                autoFocus
                className={styles.search}
                onChange={setSearch}
                placeholder={t('dropdown.search')}
                value={search}
              />
            ))}
          <div className={styles.items}>
            {filteredData.map((value, index) => (
              <Menu.MenuItem
                className={styles.menuItem}
                key={index}
                label={renderValue(value, renderValueContent)}
                leading={
                  isMultipleSelection ? (
                    <Checkbox checked={selectedValues.includes(value)} decorative />
                  ) : undefined
                }
                onClick={() => handleSelect(value)}
                role="menuitem"
              />
            ))}
          </div>
          {isMultipleSelection && props.onReset && (
            <div className={styles.reset} onClick={props.onReset}>
              <Button small variant="secondary">
                {t('dropdown.reset')}
              </Button>
            </div>
          )}
        </Menu.Menu>
      )}
    </div>
  )
}

export default DropDown
